import Purchase from '@/hooks/purchase/purchase';
import { Prices } from '@/store/prices/types';
import { PurchaseParams, RouteSpecificPurchaseParams } from '@/store/purchaseParams/types';
import { User } from '@/store/user/types';
import getParamAsString from '@/utils/getParamsAsString';

import { userCanTrial } from '../subscriptions';

export const oneTimePurchasePlans = ['sevenMin'] as const;
const validPlans = ['monthly', 'yearly', 'quarterly', 'family', 'lifetime', ...oneTimePurchasePlans] as const;
export type ValidPlan = (typeof validPlans)[number];
export const isValidPlan = (value: string): value is ValidPlan => validPlans.includes(value as ValidPlan);

export type PurchaseQueryParams = {
	plan?: string | null;
	trialLength?: string | null;
	utm_medium?: string | null;
	coupon?: string | null;
	promotion?: string | null;
	guestPass?: string | null;
	calmjwt?: string | null;
	is30DayOverride?: string | null;
};

function getPlan(plan: string | null): ValidPlan {
	if (plan && isValidPlan(plan)) {
		return plan;
	}
	return 'yearly';
}

const getPromotion = (promotion: string | null, isPaidTraffic: boolean, isTrialFlow: boolean) => {
	if (isPaidTraffic && isTrialFlow) {
		return '7_day_free_trial';
	}

	return promotion;
};

const initCouponValue = ({
	plan,
	coupon,
	prices,
}: {
	plan: ValidPlan;
	coupon?: string | null;
	prices: Prices | null;
}): string | null => {
	if (prices?.coupons[plan || 'yearly']) {
		return prices?.coupons[plan || 'yearly']?.id || null;
	}

	return getParamAsString(coupon) || null;
};

export function initPurchaseParams({
	query,
	prices,
	user,
	routeSpecificPurchaseParams,
}: {
	query: PurchaseQueryParams;
	prices: Prices | null;
	user: User | null;
	routeSpecificPurchaseParams?: RouteSpecificPurchaseParams | null;
}): PurchaseParams {
	const plan = getPlan(routeSpecificPurchaseParams?.plan || getParamAsString(query.plan));
	const customTrialLength = Number(getParamAsString(query.trialLength));
	const trialLengthInt = Number.isNaN(customTrialLength) ? null : customTrialLength;
	const isPaidTraffic = query.utm_medium === 'paid';

	// this trial determination logic is brutal - need to reduce to a single source of truth
	const noTrial = !userCanTrial(user) && query.utm_medium !== 'second_free_trial';
	const isTrialFlow = Boolean(routeSpecificPurchaseParams?.hasFreeTrial && !noTrial);

	const initialPurchaseParams: PurchaseParams = {
		plan,
		coupon: routeSpecificPurchaseParams?.coupon ?? initCouponValue({ coupon: query.coupon, prices, plan }),
		promotion:
			routeSpecificPurchaseParams?.promotion ??
			getPromotion(getParamAsString(query.promotion), isPaidTraffic, isTrialFlow),
		samsungGUID: routeSpecificPurchaseParams?.samsungGUID ?? null,
		guestPass: routeSpecificPurchaseParams?.guestPass ?? getParamAsString(query.guestPass),
		calmjwt: routeSpecificPurchaseParams?.calmjwt ?? getParamAsString(query.calmjwt),
		is30DayOverride:
			routeSpecificPurchaseParams?.is30DayOverride ?? Boolean(getParamAsString(query.is30DayOverride)),
		giftPrice: routeSpecificPurchaseParams?.giftPrice ?? null,
		isUpdateBillingScreen: routeSpecificPurchaseParams?.isUpdateBillingScreen ?? false,
		trialLengthOverride: trialLengthInt, // Only to be used for initialization, use `purchaseType.duration` for logic
	};

	const purchaseType = prices
		? new Purchase({
				...initialPurchaseParams,
				abTests: {},
				prices,
				user,
				isPaidTraffic,
				freeTrialLength: routeSpecificPurchaseParams?.freeTrialLength ?? null,
				hasFreeTrial: routeSpecificPurchaseParams?.hasFreeTrial ?? null,
				freeTrialLengthUnit: routeSpecificPurchaseParams?.freeTrialLengthUnit ?? null,
				noTrial,
			}).purchaseType
		: null;

	return {
		purchaseType,
		...initialPurchaseParams,
	};
}
